import React from 'react'
import { useIntl } from 'react-intl'

import { SectionCurve } from '../../../components/SectionCurve'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { useRoutes } from '../../../routes'
import { SectionTestimonials } from '../../home/SectionTestimonials'
import { Section3CardsBenefits } from '../Section3CardsBenefits'
import { SectionGridBenefits } from '../SectionGridBenefits'
import { SectionHero } from '../SectionHero'
import { SectionQuote } from '../SectionQuote'
import { SectionSwitchedBenefits } from '../SectionSwitchedBenefits'
import { SectionTable } from '../SectionTable'

export default function CompareToNotionPage() {
  const routes = useRoutes()
  const intl = useIntl()

  const title = intl.formatMessage({
    defaultMessage: 'Slite - Best Notion alternative for team knowledge',
    id: 'compare.notion.page.seo.title',
  })

  const description = intl.formatMessage({
    defaultMessage: `Slite is designed for team knowledge management. Put all the chores of creating, managing and finding trusted company information on autopilot. Import your Notion docs and try it for free.`,
    id: 'compare.notion.page.seo.description',
  })

  return (
    <SlitePage
      canonicalUrl={routes.compare.detail({ slug: 'alternative-to-notion' })}
      background="moon-dust"
      title={title}
      description={description}
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/8d982c7b7838a30908fe4c4b0d52c1eec66f55ff-2100x1200.webp"
    >
      <SectionHero
        competitorName="Notion"
        title={intl.formatMessage({
          defaultMessage:
            'Best Notion alternative designed for team knowledge management.',
          id: 'compare.notion.page.title',
        })}
        subtitle={intl.formatMessage({
          defaultMessage:
            'High-growth teams need a dedicated knowledge management tool to scale efficiently, not a generic all-in-one solution that slows them down. See why thousands of companies choose Slite over Notion to finally give their teams a single source of truth.',
          id: 'compare.notion.page.subtitle',
        })}
      />

      <SectionCurve from="moon-dust" to="shade-100" shape="top-large" />

      <Section3CardsBenefits
        title={intl.formatMessage(
          {
            defaultMessage: 'Why are teams choosing {br} Slite over Notion?',
            id: 'compare.notion.featureSection.title',
          },
          {
            br: <br className="display-none-small" />,
          }
        )}
        benefits={[
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/2789278e652e7ced21b78f06135061c88b8b6dcf-1382x1200.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Search illustration',
              id: 'compare.notion.feature1.image.alt',
            }),
            imageBackground: 'saturn-orange',
            title: intl.formatMessage({
              defaultMessage: 'Search that just works.',
              id: 'compare.notion.feature1.title',
            }),
            description: intl.formatMessage({
              id: 'compare.notion.feature1.description',
              defaultMessage: `For a tool that's intended to boost productivity, search shouldn't get in the way. With Slite, enjoy lightning-fast search if you already know a doc you're looking for or just ask our AI assistant and get the answer in an instant.`,
            }),
          },
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/2fd9add440cfb47907995779a786bc8d0b4ae8ee-691x359.webp',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Version history illustration',
              id: 'compare.notion.feature1.versionHistory.image.alt',
            }),
            imageBackground: 'pink-moon',
            title: intl.formatMessage({
              id: 'compare.notion.feature2.versionHistory.title',
              defaultMessage: 'Always up-to-date.',
            }),
            description: intl.formatMessage({
              id: 'compare.notion.feature2.versionHistory.description',
              defaultMessage:
                'With Notion maintaining your knowledge up to date is a huge manual chore. Slite gives you one central place to keep your knowledge base fresh, at scale. Take AI-suggested actions with one click or slice and dice the way you want to.',
            }),
          },
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/94139512ad92efef586ef8eb19e9529eccac3e19-800x619.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Easy setup illustration',
              id: 'compare.notion.feature3.image.alt',
            }),
            imageBackground: 'primary',
            title: intl.formatMessage({
              id: 'compare.notion.feature3.title',
              defaultMessage: 'Easy to set up and master.',
            }),
            description: intl.formatMessage({
              defaultMessage:
                "Notion takes pride in its deep customization but that's also the reason why only power users can master it. Slite is opinionated and so intuitive that it's easy to adopt for everyone, even those who are not tech-savvy.",
              id: 'compare.notion.feature3.description',
            }),
          },
        ]}
      />

      <SectionGridBenefits
        title={intl.formatMessage(
          {
            defaultMessage: 'Say goodbye to outdated {br} docs and clutter',
            id: 'compare.notion.benefitsSection.title',
          },
          {
            br: <br className="display-none-small" />,
          }
        )}
        data={[
          {
            title: intl.formatMessage({
              defaultMessage: 'How will I know if a doc is up to date?',
              id: 'compare.notion.grid.benefit1.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Use doc verification status and let your team know what they can trust straight away.',
              id: 'compare.notion.grid.benefit1.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/cb7129111fa7ce083b3be2b2215303a6ce3fe23b-1375x852.jpg',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Doc verification status illustration',
              id: 'compare.notion.grid.benefit1.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'Yes, but it will still get outdated at some point...',
              id: 'compare.notion.grid.benefit2.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Choose different validity periods and get automatic reminders when your docs have to be reviewed.',
              id: 'compare.notion.grid.benefit2.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/9c6a09e0aaca7994ab26260d26050eddc1a06820-1375x1120.png',
            imageClassnames: 'padding-horizontal-24',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Doc review reminder illustration',
              id: 'compare.notion.grid.benefit2.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'And  what about the docs that are not relevant anymore?',
              id: 'compare.notion.grid.benefit3.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Discover inactive docs directly from the sidebar, with a single click. Keep, revive or archive with confidence - you can always access them exactly where they belong.',
              id: 'compare.notion.grid.benefit3.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/4b8f2c887b485cbc926d81dd14580e6adeee4809-1375x852.jpg',
            imageClassnames: 'padding-left-24 flexbox flexbox-justify-end',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Inactive docs illustration',
              id: 'compare.notion.grid.benefit3.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                "It's just impossible to manually find and update all my docs.",
              id: 'compare.notion.grid.benefit4.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'We find them for you. Slice and dice all your docs, take AI-suggested actions and act on them in bulk - all in one central panel.',
              id: 'compare.notion.grid.benefit4.description',
            }),
            imageClassnames:
              'background-saturn-orange-lighter padding-left-24 flexbox flexbox-justify-end',
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/1aa50d8a526972c03ba9ece421d3038fa1d818b6-1375x864.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Bulk actions illustration',
              id: 'compare.notion.grid.benefit4.image.alt',
            }),
          },
        ]}
      />

      <SectionCurve from="shade-100" to="saturn-orange" shape="top-large" />
      <SectionQuote
        background="saturn-orange"
        quote={intl.formatMessage({
          defaultMessage: `"So exciting to see a company in doc/productivity space to understand it's PMF and stick to where it can shine. The doc status is awesome and is one of the places where Notion falls short. The notification to hint what docs are out-of-date is really clever. Well done!"`,
          id: 'compare.notion.quote.mattRoskovec.text',
        })}
        authorImageUrl="https://cdn.sanity.io/images/dnsgqii5/production/4617f13e3beaa739bd78a47a7d702720f9136b60-240x240.webp"
        authorTitle="Matt Roskovec"
        authorDescription="Product manager, Pitch"
      />
      <SectionCurve
        from="moon-dust"
        to="saturn-orange"
        shape="top-large"
        style={{ transform: 'translateY(-1px) rotate(180deg)' }}
      />

      <SectionSwitchedBenefits
        title={intl.formatMessage({
          defaultMessage: 'Sounds great, but I have a few doubts...',
          id: 'compare.notion.faqSection.title',
        })}
        data={[
          {
            title: intl.formatMessage({
              defaultMessage: 'Can Slite really give answers in an instant?',
              id: 'compare.notion.faq1.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'It sure can! Stay in your workflow and get trusted instant answers based on your verified docs wherever you work - ask our AI assistant directly in Slite, use Slack integration or add Chrome browser extension.',
              id: 'compare.notion.faq1.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/fb4f0f8895dad53025e2a3e530d21f1e29d1fd98-1396x723.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'AI assistant illustration',
              id: 'compare.notion.faq1.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                "Wait, but isn't Notion also offering similar AI-powered answers?",
              id: 'compare.notion.faq2.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                "It can look that way at first, but there's one key difference - Slite provides you answers based on verified docs, ensuring your team gets relevant and reliable information. Notion, on the other hand, doesn't pay attention if the source is actually trustworthy.",
              id: 'compare.notion.faq2.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/9ecd2988c0a8251600d48d2ac0ec99ffc71dcf5c-1396x723.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Verified docs illustration',
              id: 'compare.notion.faq2.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage: "But don't you have to pay extra for AI?",
              id: 'compare.notion.faq3.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                "Not really, at least with Slite. It costs extra as an add-on in Notion, but it's included in any paid plan in Slite. And Slite still comes out 20% less expensive than Notion's Business plan.",
              id: 'compare.notion.faq3.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/e4104b70a4b970d2d9889cba96b65f0c505639a2-1396x723.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Pricing illustration',
              id: 'compare.notion.faq3.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'What if some of our teams are already using Notion?',
              id: 'compare.notion.faq4.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Too often we hear from teams that Notion becomes too complex and can only be mastered by power users, which ends up with only tech-savvy teams leveraging it to its full potential. Slite is powerful enough for advanced needs but still easy to set up and use for everyone. No more siloed teams using different tools.',
              id: 'compare.notion.faq4.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/fa51bf7dd0c15400b6e544db4d541517284b91be-1396x723.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Easy to use illustration',
              id: 'compare.notion.faq4.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                "But isn't Notion also suitable for project management?",
              id: 'compare.notion.faq5.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'It is, to a certain level. However, many companies that transitioned from Notion agree that as your business expands, relying on a dedicated tool for project management becomes essential. The good news is, Slite seamlessly integrates with various project management tools to meet those needs.',
              id: 'compare.notion.faq5.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/67fb889b3d6279d455f2edee615ad84d5543db24-1396x723.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Integrations illustration',
              id: 'compare.notion.faq5.image.alt',
            }),
          },
        ]}
      />
      <SectionCurve from="moon-dust" to="shade-00" shape="top-large" />
      <SectionTable
        competitorName="Notion"
        title={intl.formatMessage({
          defaultMessage:
            'Notion does a lot, but none of it is built for knowledge management.',
          id: 'compare.notion.comparisonTable.title',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'No verification, no content health dashboard, no enterprise search. Slite is purpose-built to keep your team knowledge accurate and accessible.',
          id: 'compare.notion.comparisonTable.description.v2',
        })}
        checkedFeatures={[
          'company-wiki-templates',
          'mcp-integration',
          'ai-workflows',
        ]}
      />
      <SectionCurve
        from="moon-dust"
        to="shade-00"
        shape="top-large"
        style={{ transform: 'translateY(-1px) rotate(180deg)' }}
      />
      <SectionTestimonials withCaseStudies={false} />
      <SectionLastCTA background="moon-dust" competitorName="Notion" />
    </SlitePage>
  )
}
